import { useMemo, useRef, useState } from 'react';
import { SortableTable } from './SortableTable.jsx';
import {
  arrivingComments, commentFilters, commentStatus, filterComments, formatAgo, labelsFor, sampleComments, savedReplies, tiktokHandle,
} from './tiktokComments.js';

/** Things in a row that do their own job when clicked, so they must not also open or close the comment. */
const ownClicks = 'a, button, input, select, textarea, label, details';

function Labels({ comment, fresh }) {
  const { personal, housing } = labelsFor(comment.text);
  if (!fresh && !personal && !housing) return null;
  return (
    <span className="admin-badges">
      {fresh && <span className="admin-badge is-staff">New</span>}
      {housing && <span className="admin-badge">Housing question</span>}
      {personal && <span className="admin-badge is-alert">{comment.hidden ? 'Personal details' : 'Personal details · hide?'}</span>}
    </span>
  );
}

/** The comment opened in place: the thread, what staff have done, and the reply box with saved replies and the actions. */
function CommentDetail({ comment, draft, now, onDraft, onReply, onLike, onHide, onDone }) {
  const pick = (text) => onDraft(text);
  return (
    <div className="admin-tiktok-detail">
      <ol className="admin-tiktok-thread" aria-label={`Comment thread from ${comment.author}`}>
        <li>
          <span className="admin-tiktok-who">{comment.author}</span> <span className="admin-sub admin-inline">{formatAgo(comment.created_at, now)}</span>
          <p className={comment.hidden ? 'is-hidden' : ''}>{comment.text}</p>
          {comment.hidden && <span className="admin-sub">Hidden: only {comment.author} and staff can see it.</span>}
        </li>
        {comment.replies.map((reply) => (
          <li key={reply.id} className="is-reply">
            <span className="admin-tiktok-who">{reply.author}</span>
            {' '}<span className="admin-sub admin-inline">{`${reply.staff ? `by ${reply.staff} · ` : ''}${formatAgo(reply.created_at, now)}`}</span>
            <p>{reply.text}</p>
          </li>
        ))}
      </ol>
      <div className="admin-tiktok-saved">
        <span className="admin-sub admin-inline">Saved replies</span>
        {savedReplies.map(([key, label, text]) => (
          <button key={key} type="button" className="button button-secondary admin-tiktok-chip" onClick={() => pick(text)}>{label}</button>
        ))}
      </div>
      <label className="admin-tiktok-reply">
        <span className="admin-sub">{`Reply to ${comment.author}`}</span>
        <textarea rows={3} value={draft} maxLength={150} onChange={(event) => onDraft(event.target.value)} placeholder="Write a reply…"/>
        <span className="admin-sub">{`${draft.length} of 150 characters`}</span>
      </label>
      <div className="admin-tiktok-actions">
        <button type="button" className="button button-dark" onClick={onReply}>Reply</button>
        <button type="button" className="button button-secondary" onClick={onLike}>{comment.liked ? 'Unlike' : 'Like'}</button>
        <button type="button" className="button button-secondary" onClick={onHide}>{comment.hidden ? 'Unhide' : 'Hide'}</button>
        <button type="button" className="button button-secondary" onClick={onDone}>{comment.done ? 'Reopen' : 'Mark done'}</button>
      </div>
      {comment.activity.length > 0 && (
        <ul className="admin-tiktok-log" aria-label="What staff have done">
          {comment.activity.map((entry, index) => (
            <li key={`${entry.what}-${index}`} className="admin-sub">{`${entry.what} by ${entry.by} · ${formatAgo(entry.at, now)}`}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Comments on our own TikTok videos, in one queue: who said what on which video, labelled when it asks about housing or shows
 * someone's personal details, and opened in place to reply, like, hide or mark done.
 * Until TikTok approves our app (docs/tiktok-comments-api.md) this shows sample comments, and everything done here only changes this
 * page; nothing is sent to TikTok. `staffName` is who the actions are recorded as.
 */
export default function TikTokTab({ staffName = 'you' }) {
  const [now] = useState(() => Date.now());
  const [comments, setComments] = useState(() => sampleComments(now));
  const arriving = useRef(arrivingComments(now));
  const [fresh, setFresh] = useState(() => new Set());
  const [filter, setFilter] = useState('needs_reply');
  const [openId, setOpenId] = useState('');
  const [draft, setDraft] = useState('');
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  const { rows, counts } = useMemo(() => filterComments(comments, filter), [comments, filter]);

  function choose(next) {
    setFilter(next);
    setOpenId('');
    setNotice('');
    setError('');
  }

  /** Changes one comment, and records what was done and by whom. */
  function change(id, update, what) {
    setComments((current) => current.map((comment) => (comment.id === id
      ? { ...update(comment), activity: [{ what, by: staffName, at: new Date().toISOString() }, ...comment.activity] }
      : comment)));
  }

  function open(comment) {
    const opening = openId !== comment.id;
    setOpenId(opening ? comment.id : '');
    setDraft('');
    setError('');
    if (opening) setFresh((current) => { const next = new Set(current); next.delete(comment.id); return next; });
  }

  function reply(comment) {
    const text = draft.trim();
    if (!text) { setError('Write a reply first, or pick a saved reply.'); return; }
    change(comment.id, (current) => ({
      ...current,
      replies: [...current.replies, { id: `${current.id}-r${current.replies.length + 1}`, author: 'residentservice360', staff: staffName, text, created_at: new Date().toISOString() }],
    }), 'Replied');
    setNotice(`Reply posted to ${comment.author}. It's under Replied now.`);
    setError('');
    setDraft('');
    setOpenId('');
  }

  function hide(comment) {
    change(comment.id, (current) => ({ ...current, hidden: !current.hidden }), comment.hidden ? 'Unhidden' : 'Hidden');
    setNotice(comment.hidden
      ? `${comment.author}'s comment is visible on TikTok again.`
      : `${comment.author}'s comment is hidden. Only they and staff can see it now.`);
    setOpenId('');
  }

  function like(comment) {
    change(comment.id, (current) => ({ ...current, liked: !current.liked, likes: current.likes + (current.liked ? -1 : 1) }), comment.liked ? 'Unliked' : 'Liked');
  }

  function done(comment) {
    change(comment.id, (current) => ({ ...current, done: !current.done }), comment.done ? 'Reopened' : 'Marked done');
    setNotice(comment.done ? `${comment.author}'s comment is back in the queue.` : `${comment.author}'s comment is marked done.`);
    setOpenId('');
  }

  function arrive() {
    const next = arriving.current.shift();
    if (!next) { setNotice('No more sample comments to add.'); return; }
    const comment = { ...next, created_at: new Date().toISOString() };
    setComments((current) => [comment, ...current]);
    setFresh((current) => new Set(current).add(comment.id));
    setNotice(`New comment from ${comment.author} on "${comment.video.caption}".`);
  }

  const rowProps = (comment) => {
    const isOpen = openId === comment.id;
    return {
      className: `admin-email-row${isOpen ? ' is-open' : ''}`,
      onClick: (event) => {
        const own = event.target.closest?.(ownClicks);
        if (own && !own.classList.contains('admin-email-toggle')) return;
        open(comment);
      },
    };
  };

  const columns = [
    {
      key: 'when',
      label: 'When',
      value: (comment) => comment.created_at,
      first: 'desc',
      cell: (comment) => (
        <span className="admin-nowrap">
          <button type="button" className="admin-email-toggle" aria-expanded={openId === comment.id} aria-label={`Open the comment from ${comment.author}`}>
            <span aria-hidden="true">{openId === comment.id ? '▾' : '▸'}</span>
          </button>
          {formatAgo(comment.created_at, now)}
        </span>
      ),
    },
    {
      key: 'video',
      label: 'Video',
      value: (comment) => comment.video.caption,
      cell: (comment) => (
        <>
          {comment.video.caption}
          <span className="admin-sub">{`Posted ${formatAgo(comment.video.posted_at, now)}`}</span>
        </>
      ),
    },
    {
      key: 'comment',
      label: 'Comment',
      value: (comment) => comment.author,
      cell: (comment) => (
        <div className="admin-tiktok-comment">
          <span className="admin-tiktok-who">{comment.author}</span>
          <span className={comment.hidden ? 'is-hidden' : ''}>{comment.text}</span>
          <Labels comment={comment} fresh={fresh.has(comment.id)}/>
        </div>
      ),
    },
    { key: 'likes', label: 'Likes', value: (comment) => comment.likes, first: 'desc', cell: (comment) => `${comment.likes}${comment.liked ? ' · liked' : ''}` },
    {
      key: 'status',
      label: 'Status',
      value: (comment) => commentStatus(comment).label,
      cell: (comment) => { const { label, tone } = commentStatus(comment); return <span className={`admin-status is-${tone}`}>{label}</span>; },
    },
  ];

  return (
    <>
      <div className="admin-record-head">
        <h2 className="admin-section">{`TikTok comments · ${tiktokHandle}`}</h2>
        <div className="admin-head-actions">
          <button type="button" className="button button-secondary" onClick={arrive}>Show a new sample comment</button>
          <button
            type="button"
            className="button button-secondary"
            onClick={() => setNotice('Connecting asks TikTok to let Resident Service 360 Comments manage comments on our account. It works once TikTok approves our app.')}
          >
            Connect TikTok
          </button>
        </div>
      </div>
      <div className="mod-filters">
        <label>Comments
          <select value={filter} onChange={(event) => choose(event.target.value)}>
            {commentFilters.map(([value, words]) => <option key={value} value={value}>{`${words} (${counts[value]})`}</option>)}
          </select>
        </label>
      </div>
      {notice && <p className="admin-notice" role="status">{notice}</p>}
      {rows.length === 0
        ? <p className="admin-empty">Nothing here.</p>
        : (
          <SortableTable
            columns={columns}
            rows={rows}
            rowKey={(comment) => comment.id}
            rowProps={rowProps}
            detail={(comment) => (openId === comment.id
              ? (
                <>
                  {error && <p className="mod-error" role="alert">{error}</p>}
                  <CommentDetail
                    comment={comment}
                    draft={draft}
                    now={now}
                    onDraft={(text) => { setDraft(text); setError(''); }}
                    onReply={() => reply(comment)}
                    onLike={() => like(comment)}
                    onHide={() => hide(comment)}
                    onDone={() => done(comment)}
                  />
                </>
              )
              : null)}
          />
        )}
    </>
  );
}
